/**
 * ★ 對戰畫面的狀態機（純函式 reducer）。
 *
 * 為什麼要抽成純 reducer：
 *  1. **可測試**。同時出手遊戲的資訊邊界是安全性問題，不是 UI 問題。
 *     把它做成純函式，就可以用測試「證明」對手的 pending 動作永遠不會進入 state
 *     （見 machine.test.ts 的 no-leak 測試），而不是靠人眼審 JSX。
 *  2. **重連的正確性**。斷線重連會收到亂序/重複的事件；把順序保證集中在一個地方，
 *     比散落在各個 useEffect 裡好除錯太多。
 *  3. 和 C01 的引擎一樣是純函式 —— 前後端用同一種思考方式。
 *
 * 不可違反的規則（對應 CONSTRAINTS #12、C01 §4.2 步驟 3）：
 *   在 `round_resolved` 之前，**state 裡不得有任何欄位描述對手這一回合的動作**，
 *   也不得有欄位描述「對手是否已經出手」。後者雖然後端會透過 `waiting_for` 告訴我們，
 *   但把它畫出來等於把 UI 變成一條時序旁通道 —— 我們選擇不接收它。
 */

import type {
  ActionKey,
  Fairness,
  HistoryEntry,
  MatchStatus,
  MatchView,
  Seat,
  SeatInfo,
  ServerEventMessage,
} from '../api/types';
import { isTerminalStatus } from '../api/types';

// ---------------------------------------------------------------------------
// 狀態
// ---------------------------------------------------------------------------

export type MatchPhase =
  /** 還沒有對局：剛進畫面 / 已離開 */
  | 'idle'
  /** 已知道 match_id，正在載入 Observation / 建立 WS */
  | 'connecting'
  /**
   * 對局存在但還沒開打。**兩種後端狀態共用這一個 phase**：
   *
   * | `status` | 後端語意 | 玩家在等什麼 |
   * | :-- | :-- | :-- |
   * | `waiting` | 座位還沒滿（`start_if_ready` 的 `len(seats) < 2`） | 等**媒合**找到對手 |
   * | `ready` | 座位滿了、還沒開局 | 等對手**連上線**（C02 §6.3 的訂閱條件） |
   *
   * ★ 共用 phase 是**刻意**的，不是把兩者混為一談：
   * 對畫面而言兩者完全一樣（不能出手、沒有倒數、等待中），
   * 而兩者的區別完整保留在 `state.status` 上，需要的人讀得到。
   * 新增一個 phase 會多出一個沒有人在渲染的分支 —— 那是空白畫面，不是精確。
   */
  | 'waiting_opponent'
  /** 回合進行中，我還沒出手 —— 三顆大按鈕可按 */
  | 'choosing'
  /** 我按了按鈕，POST/WS 還沒回 ack */
  | 'submitting'
  /** ★ 我已出手，等待對手。這個畫面**不得洩漏任何對手資訊** */
  | 'submitted'
  /** 收到 round_resolved，揭曉動畫播放中 */
  | 'revealing'
  /** 揭曉完畢，等下一回合 round_started */
  | 'round_summary'
  /** 對局結束 */
  | 'ended'
  /** 管理員作廢 */
  | 'voided';

export type ConnectionPhase = 'connecting' | 'online' | 'reconnecting' | 'offline';

export interface ResolvedRound {
  roundNo: number;
  /** 兩邊的動作。**只有在 round_resolved 之後才會被填**。 */
  actions: Record<string, ActionKey>;
  winner: Seat | null;
  scores: Record<string, number>;
}

// ---------------------------------------------------------------------------
// ★ 對局結果：三態，不是兩態（C02 §6.1 v1.2）
// ---------------------------------------------------------------------------

/**
 * 為什麼不能只用 `Seat | null`：
 *
 * C02 §6.1 是一條**強制**規則 —— 客戶端必須區分
 *
 * * 「`winner_seat` 這個欄位不存在」⇒ **協定不相容**，記錄錯誤並顯示「結果未知」，
 *   **不得**退化成和局；
 * * 「`winner_seat` 存在且為 `null`」⇒ 真正的平手。
 *
 * 舊版把兩者都編碼成 `finalWinner: null`，於是前者**安靜地偽裝成後者** ——
 * 那就是 FINDINGS F-4：後端送 `winner_seat`、前端讀 `winner`，
 * **每一場對局的結束標題都顯示「和局」，不分輸贏**，而 84 個前端測試全綠。
 *
 * 把「我讀不到」做成一個型別上無法被誤認成「平手」的值，
 * 是讓同一類缺陷**不可能**再安靜發生的唯一辦法。
 */
export type MatchOutcomeUnknownReason =
  /** payload 裡根本沒有 `winner_seat` 這個 key（多半是後端版本不相容）。 */
  | 'winner_seat_absent'
  /** key 在，但值既不是 null 也不是合法座位（例如字串 `"0"`）。 */
  | 'winner_seat_malformed'
  /** 對局已結束，但我們從來沒拿到任何結束資訊（例如 abandoned 的 hydrate）。 */
  | 'no_end_information';

export type MatchOutcome =
  | { kind: 'decided'; winner: Seat }
  | { kind: 'draw' }
  | { kind: 'unknown'; reason: MatchOutcomeUnknownReason };

/** UI 只會看到這四種。**沒有第五種，也沒有「預設當成和局」。** */
export type MatchResult = 'win' | 'lose' | 'draw' | 'unknown';

/**
 * 後端送來的東西與契約對不起來時記進 `state.error` 的錯誤碼。
 *
 * 兩條契約規則都要求「必須記錄錯誤」，共用同一個碼：
 *   * C02 §6.1 —— `match_ended` 讀不到 `winner_seat`；
 *   * C02 §6.2 —— `round_started` 沒帶 `deadline_at`。
 *
 * reducer 是純函式（見檔頭），所以錯誤**記進 state**，
 * 由 `useMatch` 的副作用層轉成 `console.error` / telemetry。
 */
export const PROTOCOL_MISMATCH = 'PROTOCOL_MISMATCH';
/** `rematch_ready` 缺少可用的 `new_match_id` 時的錯誤碼（C02 §5）。 */
export const REMATCH_EVENT_INVALID = 'REMATCH_EVENT_INVALID';

// ---------------------------------------------------------------------------
// ★ 回合期限的來源（C02 §6.2 v1.3）
// ---------------------------------------------------------------------------

/**
 * `state.deadlineAt` 這個值是怎麼來的。
 *
 * ⚠️ **UI 一律只讀 `deadlineAt`**；這個欄位是**診斷與測試**用的，
 * 不是第二個真相來源。它存在的理由是：退化推導出來的期限與後端直接給的期限
 * 在**值**上可能一模一樣（正常情況下本來就該一樣），
 * 若不把來源記下來，測試就無法區分「前端用了契約要求的欄位」與
 * 「前端其實在自己猜，只是剛好猜對」—— 而那個區別正是 A-1 的全部內容。
 */
export type DeadlineSource =
  /** `round_started.payload.deadline_at`：C02 §6.2 要求的正常路徑。 */
  | 'event'
  /** `GET /matches/{id}` / WS `sync` 的**頂層** `deadline_at`（契約保證是絕對時刻）。 */
  | 'view'
  /**
   * ★ `admin_action{action:"extend_deadline"}.deadline_at`（C02 §7）。
   *
   * 管理員延長時限之後，期限的真相來源就不再是 `round_started` 那一個了 ——
   * 後端已經把 `match.deadline_at` 改寫，伺服器是照**新**期限裁定的。
   * 單獨記一種來源，是為了讓「畫面上的倒數到底聽誰的」在診斷時看得出來。
   */
  | 'admin'
  /** ★ §6.2 的退化路徑：`occurred_at + move_timeout_ms`。同時一定會記錯誤。 */
  | 'derived'
  /** 現在本來就沒有期限（尚未開局 / 已結束 / 已作廢）。 */
  | 'none'
  /** 需要期限卻連退化推導都做不到（缺 `occurred_at` 或 `move_timeout_ms`）。 */
  | 'unavailable';

/**
 * C02 §6.2 缺 `deadline_at` 但**推導成功**時的錯誤訊息。
 *
 * 匯出成常數是為了讓測試引用它，而不是手抄一份字串 ——
 * 手抄的斷言只會證明「兩邊抄得一樣」，不會證明任何行為。
 */
export const DEADLINE_DERIVED_MESSAGE =
  'round_started: deadline_at absent — derived from occurred_at + move_timeout_ms (C02 §6.2)';

/** C02 §6.2 缺 `deadline_at` 且**推導不出來**時的錯誤訊息。 */
export const DEADLINE_UNAVAILABLE_MESSAGE =
  'round_started: deadline_at absent and not derivable from occurred_at + move_timeout_ms (C02 §6.2)';

/**
 * `admin_action{action:"extend_deadline"}` 少了可用 `deadline_at` 時的錯誤訊息。
 *
 * ★ 這裡**不退化推導**（與 §6.2 的 `round_started` 不同）：
 * 「延長多久」只有後端知道，`extra_seconds` 從來不在事件裡（C02 §7.1 只有
 * 請求 body 有它，事件 payload 送的是算好的絕對時刻）。
 * 猜一個期限出來，猜短了就是把玩家的倒數往回撥 —— 那正是這條路徑要修的傷害本身。
 */
export const ADMIN_DEADLINE_INVALID_MESSAGE =
  'admin_action(extend_deadline): unusable deadline_at — countdown left unchanged (C02 §7)';

/** `admin_action{action:"message"}` 少了可用 `message` 時的錯誤訊息。 */
export const ADMIN_MESSAGE_INVALID_MESSAGE =
  'admin_action(message): empty or malformed message — announcement dropped (C02 §7.1)';

/**
 * ★ 座位 `kind` 讀不懂時的錯誤訊息前綴（後面接上線路上真正的字串）。
 *
 * 為什麼「讀不懂」一定要吵：C02 §6.1 的規則本體 ——
 * 把「我讀不到」和「真的是 X」編碼成同一個值，前者就會安靜地偽裝成後者。
 * 舊實作把不認得的 kind 一律改寫成 `'guest'`，於是後端送來的 `"human"`
 * （每一個 Keycloak 登入玩家）在 `state.seats` 裡都變成訪客，**而且沒有任何人被通知**。
 *
 * 這條路徑**不 fail-open 也不 fail-closed**：座位照收（少一個 kind 不該讓畫面消失），
 * 但 kind 標成 `null` + 記這個錯誤。詳見 `api/types.ts` 的 `SeatInfo.kind`。
 */
export const SEAT_KIND_UNKNOWN_MESSAGE =
  'seats[]: unrecognized seat kind — kept as unknown, not rewritten (C02 §5 / C04 §1.2)';

/**
 * ★ 對局內公告（C02 §7.1 `POST /matches/{id}/message`，需求 1.2.3「賽中：公告」）。
 *
 * 線路上的形狀是 `admin_action{action:"message", message:"…"}`，
 * 由 `admin/routes_game_admin.py` 用 `Hub.publish_raw()` 直接發到對局頻道。
 *
 * ## 這是 FE-A（本檔）與 FE-B（`MatchPage`）之間的介面
 *
 * reducer 只負責把公告**收進 state 並保持順序**；
 * 「顯示在哪裡、用什麼樣式、要不要自動消失」全部是 `MatchPage` 的事。
 * 與 `rematchMatchId`（導頁目標）是同一種分工：**狀態在這裡，渲染在那裡。**
 */
export interface MatchAnnouncement {
  /** 公告內文（C02 §7.1：1..500 字，後端已驗證非空）。 */
  message: string;
  /** 公告發生時對局所在的回合。後端送的是 envelope 的 `round_no`；沒有就是 null。 */
  roundNo: number | null;
  /**
   * 事件的 `occurred_at`（ISO-8601）。
   *
   * ⚠️ **目前恆為 `null`** —— `Hub.publish_raw()` 不補時間戳，golden file 可以作證
   * （`src/test/event-fixtures.json` 的 `wire` 區塊裡，兩個 `admin_action`
   * 都沒有 `occurred_at`）。留著這個欄位是為了：後端哪天補上，前端**不必改形狀**。
   * UI 不得假裝有時間 —— 沒有就不要顯示，不要拿本機時鐘湊一個。
   */
  occurredAt: string | null;
}

export interface MatchMachineState {
  phase: MatchPhase;
  /** 連線狀態與 phase 正交：斷線時 phase 不變（斷線不等於棄權，C02 §6.3）。 */
  connection: ConnectionPhase;
  reconnectAttempt: number;

  matchId: string | null;
  variantKey: string | null;
  status: MatchStatus | null;

  mySeat: Seat | null;
  seats: SeatInfo[];

  roundNo: number;
  legalActions: ActionKey[];
  /**
   * 本回合的出手期限，**絕對時刻**（ISO-8601 UTC）。null = 現在沒有倒數。
   *
   * C02 §6.2：期限一定是絕對時刻，不是時長 —— 客戶端無法從「還有幾秒」
   * 還原出「什麼時候到期」，因為它不知道事件是什麼時候發出的。
   */
  deadlineAt: string | null;
  /** 這個期限是哪裡來的（診斷用，見 `DeadlineSource`）。 */
  deadlineSource: DeadlineSource;
  /** 逾時後不自動棄權 —— 只把畫面切到「等待伺服器裁定」。 */
  deadlinePassed: boolean;

  /** ★ 只有「我」這一手。永遠不會有 opponentPendingAction 這種欄位。 */
  myPendingAction: ActionKey | null;
  /** 出手用的 Idempotency-Key，重試時必須沿用同一把（C02 §0）。 */
  pendingIdempotencyKey: string | null;

  lastResolved: ResolvedRound | null;
  history: HistoryEntry[];
  scores: Record<string, number>;

  fairness: Fairness;
  /**
   * ★ 對局結果。`null` = 還沒有任何結束資訊（對局進行中）。
   *
   * **刻意不叫 `finalWinner`、刻意不是 `Seat | null`** —— 舊名字與舊型別
   * 無法表達 C02 §6.1 強制的第三態「結果未知」，而任何仍然寫
   * `state.finalWinner === null ? 和局 : …` 的程式碼都必須被編譯器擋下來重寫。
   * 讀取請用 `matchResultFor()` / `selectMatchResult()`。
   */
  finalOutcome: MatchOutcome | null;
  returns: Record<string, number> | null;

  /**
   * ★ 再戰產生的新對局 id（C02 §5 v1.2 的 `rematch_ready` 事件）。
   *
   * 這個欄位是 FE-A（reducer）與 FE-B（導頁）之間的**唯一**介面：
   * reducer 只負責把它填對，`MatchPage` / `useMatch` 監看它，
   * 非 null 且不等於目前 `matchId` 時導向 `/m/<新 id>`。
   *
   * 為什麼需要它：先按再戰的人，他的 HTTP 回應在新對局建立**之前**就結束了
   * （回的是 `waiting_for_opponent` + 舊 id），這個事件是他唯一的得知途徑。
   * 沒有它，先按的人必定永久停在「等待對手同意」（FINDINGS F-3）。
   */
  rematchMatchId: string | null;

  /**
   * ★★ **最新一則公告的內文**。這是 FE-A（reducer）與 FE-B（`MatchPage`）之間
   * 由 orchestrator 指定的介面欄位：**`state.announcement: string | null`**。
   *
   * `null` = 目前沒有公告。`MatchPage.announcementText()` 讀的就是這一把 key。
   *
   * ⚠️ **這不是第二個真相來源** —— 它恆等於 `announcements` 最後一則的 `message`
   * （沒有公告時為 `null`），由同一段程式碼一起寫入，
   * 並有一支不變式測試釘住「兩者永遠一致」。分開寫的那一天就是接縫誕生的那一天。
   */
  announcement: string | null;

  /**
   * ★ 管理員的對局內公告，**依收到的順序**（C02 §7.1、需求 1.2.3）。
   *
   * `announcement` 只帶得動「最新一則的文字」；這一份保留了順序、
   * 以及每一則的 `roundNo` —— 需要顯示公告歷程或按回合對照時要用它。
   * 空陣列（不是 `null`）是「沒有公告」，UI 因此不必分辨「還沒載入」與「沒有」。
   */
  announcements: MatchAnnouncement[];

  error: { code: string; message: string } | null;
  /** 需要重新 hydrate（例如收到 ROUND_MISMATCH）。UI 看到就去打 GET /matches/{id}。 */
  needsResync: boolean;
}

export const EMPTY_FAIRNESS: Fairness = { server_seed_commit: '', server_seed: null, transcript_hash: null };

export function initialState(matchId: string | null = null): MatchMachineState {
  return {
    phase: matchId ? 'connecting' : 'idle',
    connection: 'connecting',
    reconnectAttempt: 0,
    matchId,
    variantKey: null,
    status: null,
    mySeat: null,
    seats: [],
    roundNo: 0,
    legalActions: [],
    deadlineAt: null,
    deadlineSource: 'none',
    deadlinePassed: false,
    myPendingAction: null,
    pendingIdempotencyKey: null,
    lastResolved: null,
    history: [],
    scores: {},
    fairness: EMPTY_FAIRNESS,
    finalOutcome: null,
    returns: null,
    rematchMatchId: null,
    announcement: null,
    announcements: [],
    error: null,
    needsResync: false,
  };
}

// ---------------------------------------------------------------------------
// 輸入
// ---------------------------------------------------------------------------

export type MachineAction =
  | { type: 'HYDRATE'; match: MatchView }
  | { type: 'CONNECTION'; state: ConnectionPhase; attempt?: number }
  | { type: 'SUBMIT_LOCAL'; action: ActionKey; idempotencyKey: string }
  | { type: 'SUBMIT_ACK'; roundNo: number }
  | { type: 'SUBMIT_FAILED'; code: string; message: string }
  | { type: 'SERVER_EVENT'; event: ServerEventMessage }
  | { type: 'REVEAL_DONE' }
  | { type: 'DEADLINE_PASSED' }
  | { type: 'DISMISS_ERROR' }
  | { type: 'RESET' };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function matchReducer(state: MatchMachineState, action: MachineAction): MatchMachineState {
  switch (action.type) {
    case 'RESET':
      return initialState(null);

    case 'DISMISS_ERROR':
      return { ...state, error: null };

    case 'CONNECTION':
      // ★ 只動 connection，phase 完全不碰：斷線不等於棄權，畫面上的回合狀態要留著。
      return {
        ...state,
        connection: action.state,
        reconnectAttempt: action.attempt ?? (action.state === 'reconnecting' ? state.reconnectAttempt + 1 : 0),
      };

    case 'HYDRATE':
      return hydrate(state, action.match);

    case 'SUBMIT_LOCAL': {
      if (state.phase !== 'choosing') return state;
      if (!state.legalActions.includes(action.action)) {
        return { ...state, error: { code: 'ILLEGAL_ACTION', message: action.action } };
      }
      return {
        ...state,
        phase: 'submitting',
        myPendingAction: action.action,
        pendingIdempotencyKey: action.idempotencyKey,
        error: null,
      };
    }

    case 'SUBMIT_ACK': {
      if (action.roundNo !== state.roundNo) return state; // 遲到的 ack，忽略
      if (state.phase !== 'submitting' && state.phase !== 'submitted') return state;
      return { ...state, phase: 'submitted' };
    }

    case 'SUBMIT_FAILED': {
      // ROUND_MISMATCH / ALREADY_SUBMITTED 是「我們的認知落後了」，要求重新 hydrate。
      const needsResync = action.code === 'ROUND_MISMATCH' || action.code === 'ALREADY_SUBMITTED';
      return {
        ...state,
        // ALREADY_SUBMITTED 代表伺服器其實收到了 → 停在 submitted 才是對的
        phase: action.code === 'ALREADY_SUBMITTED' ? 'submitted' : 'choosing',
        myPendingAction: action.code === 'ALREADY_SUBMITTED' ? state.myPendingAction : null,
        pendingIdempotencyKey: action.code === 'ALREADY_SUBMITTED' ? state.pendingIdempotencyKey : null,
        error: { code: action.code, message: action.message },
        needsResync,
      };
    }

    case 'REVEAL_DONE': {
      if (state.phase !== 'revealing') return state;
      // ★ `isTerminalStatus()`，不是 `=== 'ended'`：後端送的是 `completed`
      //   （以及 `abandoned` / `voided`），`'ended'` 是前端自己的 phase 名字。
      if (isTerminalStatus(state.status)) return { ...state, phase: 'ended' };
      return { ...state, phase: 'round_summary' };
    }

    case 'DEADLINE_PASSED': {
      // 逾時**不**由前端裁定。C01 §4.3：on_timeout 是伺服器的策略（預設隨機出手）。
      if (state.phase !== 'choosing' && state.phase !== 'submitting') return state;
      return { ...state, deadlinePassed: true };
    }

    case 'SERVER_EVENT':
      return applyServerEvent(state, action.event);

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// HYDRATE：從 GET /matches/{id} 或 WS 的 resync 回覆重建
// ---------------------------------------------------------------------------

function hydrate(state: MatchMachineState, m: MatchView): MatchMachineState {
  // ★ 後端的結束狀態是 `completed`（不是 `ended`）、`abandoned`、`voided`。
  //   舊版寫 `m.status === 'ended'` —— 對真後端永遠 false。
  //   在 `observation.terminal` 兜著的情況下沒有變成 bug，但那是運氣不是設計：
  //   `abandoned`（雙方都沒連上）時 observation 不是 terminal，
  //   玩家會一直等一場永遠不會開始的對局。
  const terminal = m.observation?.terminal === true || isTerminalStatus(m.status);
  const phase = derivePhaseFromView(state, m, terminal);

  // 重連時：如果我們本地已經在 revealing（動畫播到一半），不要被 hydrate 打斷。
  const keepReveal = state.phase === 'revealing' && state.matchId === m.match_id;

  const outcome = hydrateOutcome(state, m);

  // ★ HTTP 路徑也走同一個 reader。
  //   `api/types.ts` 早就寫著「解析一律走 machine.ts 的 readSeats() / readSeatEntry()」，
  //   但 hydrate 以前是直接把 `m.seats` 塞進 state —— 於是**同一個 state 欄位有兩條入口、
  //   兩套規則**：事件路徑會把不認得的 kind 改寫，HTTP 路徑原封不動放行。
  //   兩條路徑分岔正是這個 repo 反覆出現的失效模式（C02 §6 規則 3 的同一個道理：
  //   「重連後看到的畫面」與「重新整理後看到的畫面」不得不一樣）。
  //   ⚠️ 語意刻意保持與舊版一致：HYDRATE 是**整份取代**，`m.seats` 是空陣列時就取代成空陣列
  //   （不是沿用舊座位），所以這裡是 `?? []` 而不是 `?? state.seats`。
  const seatsRead = readSeats({ seats: m.seats }, state.seats);

  return {
    ...state,
    matchId: m.match_id,
    variantKey: m.variant_key,
    status: m.status,
    mySeat: m.my_seat,
    seats: seatsRead.seats ?? [],
    roundNo: m.round_no,
    legalActions: m.observation?.legal_actions ?? [],
    // `GET /matches/{id}` 的頂層 `deadline_at` 早就是絕對時刻（C02 §5/§6.2），
    // 直接用。沒有值不是缺陷 —— `waiting` / `ready` / 已結束的對局本來就沒有期限。
    deadlineAt: m.deadline_at ?? null,
    deadlineSource: m.deadline_at ? 'view' : 'none',
    deadlinePassed: false,
    // 只有在回合前進時才清掉我的出手；同一回合的 resync 要保留（避免重複出手）。
    myPendingAction: m.round_no === state.roundNo ? state.myPendingAction : null,
    pendingIdempotencyKey: m.round_no === state.roundNo ? state.pendingIdempotencyKey : null,
    history: m.history ?? state.history,
    scores: m.scores ?? state.scores,
    fairness: mergeFairness(state.fairness, m.fairness),
    finalOutcome: outcome,
    // ★ 公告是**對局層級**的東西：換了一場對局（例如再戰導頁）就不能沿用，
    //   同一場對局的 resync 則必須留著（`GET /matches/{id}` 不含公告，
    //   清掉等於每次重連都把管理員說過的話抹掉）。
    //   與 `hydrateOutcome()` 用的是同一條判斷。
    ...keptAnnouncements(state, m),
    error: outcomeError(outcome, 'observation.public') ?? seatKindError(seatsRead) ?? state.error,
    phase: keepReveal ? 'revealing' : phase,
    needsResync: false,
  };
}

/**
 * ★ 重新整理 / 重連之後也要知道誰贏了。
 *
 * 為什麼這一段非有不可：`match_ended` 是**一次性**的事件。已結束的對局若是靠
 * `GET /matches/{id}`（重新整理）或 WS 的 `{"op":"sync"}`（重連）進來的，
 * 那個事件永遠不會再出現一次。舊版的 `hydrate()` 完全不碰結果欄位 ⇒
 * 「打完當下對、F5 之後變和局」，比全錯更難查。
 *
 * 資料來源：`observation.public.winner_seat` —— 引擎在 `_end_match()` 才寫進 `public`，
 * 因此**進行中的對局根本沒有這個 key**，不會被誤判。C02 §6.3 保證
 * `GET /matches/{id}` 與 WS `sync` 走同一段投影，所以這一段同時修好兩條路徑。
 *
 * 只在 `status === 'completed'` 時要求這個欄位：`abandoned` / `voided` 的對局
 * 本來就可能沒有勝者，把它們算成「協定不相容」會製造假警報。
 */
function hydrateOutcome(state: MatchMachineState, m: MatchView): MatchOutcome | null {
  if (m.status !== 'completed') {
    // 換了一場對局（例如再戰導頁）就不能沿用上一場的結果。
    return m.match_id === state.matchId ? state.finalOutcome : null;
  }
  const pub = m.observation?.public;
  if (!pub || typeof pub !== 'object') {
    return { kind: 'unknown', reason: 'winner_seat_absent' };
  }
  const outcome = readMatchOutcome(pub as Record<string, unknown>);
  // 已經從 `match_ended` 事件拿到明確結果時，不讓一份較舊的投影把它降級成「未知」。
  if (outcome.kind === 'unknown' && state.finalOutcome && state.finalOutcome.kind !== 'unknown') {
    return m.match_id === state.matchId ? state.finalOutcome : outcome;
  }
  return outcome;
}

/**
 * ★★ R-2 的缺陷本體就在這裡。
 *
 * 舊版處理了 `voided`、terminal、`waiting`，**唯獨漏掉 `ready`** ——
 * 於是 `ready` 的對局落到最後那個 `iStillOwe` 分支。
 * 而 `ready` 的 `observation.current_players` 是**全部座位**
 * （兩個人都還沒出手，因為對局根本還沒開始），所以「我」一定在裡面：
 *
 * ```
 * 輸入：status=ready, deadline_at=null, observation.current_players=[0,1]
 * 輸出：{phase:"choosing", deadlineAt:null, canSubmit:true, legalActions:[rock,paper,scissors]}
 * ```
 *
 * 畫面上是三顆**可以按**的按鈕、沒有倒數。按下去 → 後端 `TerminalStateError`
 * → `MATCH_ALREADY_ENDED` → 409 → 紅色橫幅顯示一句**沒有翻譯**的英文，
 * 而對局其實**還沒開始** —— 訊息本身是誤導的。
 *
 * ## 為什麼這一輪才變成問題
 *
 * C02 §6.3（v1.4）之前，這個窗口只有一次 round trip（毫秒級）：
 * 第一個訂閱者就會開局。§6.3 把開局條件訂成「**每個**人類座位都至少訂閱過一次」，
 * 於是它變成**每一場排隊／再戰對局上、長達數秒的確定性窗口**。
 * 缺陷沒有變，變的是它的曝光面 —— 這正是「修好一個接縫會改變另一個接縫的後果」。
 *
 * ## `ready` 與 `waiting`：狀態不同，畫面相同
 *
 * `waiting` 是「座位還沒滿，等媒合」；`ready` 是「座位滿了，等對手連上線」。
 * 兩者對玩家的**可操作性**完全一樣（不能出手、沒有倒數），所以共用 phase；
 * 兩者的區別完整保留在 `state.status`，需要區分的人讀得到。
 */
/**
 * `announcement` 與 `announcements` **一定一起寫**。
 *
 * 兩者是同一件事的兩種形狀；分開寫的那一天就是接縫誕生的那一天，
 * 而本檔案的存在理由之一就是不要再製造那種東西。
 */
function withAnnouncements(list: MatchAnnouncement[]): Pick<
  MatchMachineState,
  'announcement' | 'announcements'
> {
  return {
    announcement: list.length > 0 ? list[list.length - 1].message : null,
    announcements: list,
  };
}

/** HYDRATE 時公告的去留：同一場留著，換了一場清空（見 `hydrate()` 的註解）。 */
function keptAnnouncements(
  state: MatchMachineState,
  m: MatchView,
): Pick<MatchMachineState, 'announcement' | 'announcements'> {
  return withAnnouncements(m.match_id === state.matchId ? state.announcements : []);
}

function derivePhaseFromView(state: MatchMachineState, m: MatchView, terminal: boolean): MatchPhase {
  if (m.status === 'voided') return 'voided';
  if (terminal) return 'ended';
  // ★ `ready` 必須在 `current_players` 之前判斷 —— 一場還沒開始的對局，
  //   「所有人都還沒出手」是**開局前的常態**，不是「輪到你了」。
  if (m.status === 'waiting' || m.status === 'ready') return 'waiting_opponent';

  const current = m.observation?.current_players ?? [];
  // C01 §2：同時出手制回傳「尚未出手」的座位集合。
  // 我不在裡面 ⇒ 我已經出手了 ⇒ submitted。
  //
  // ★ 注意：這裡**只**用「我」在不在集合裡。我們刻意不看對手在不在裡面，
  //   即使伺服器告訴我們了。理由見檔案頂端與 docs/design/ui/01-player-experience.md §4.3。
  const iStillOwe = m.my_seat !== undefined && current.includes(m.my_seat);
  if (iStillOwe) {
    // 重連前已經送出但沒收到 ack 的情況：保守地讓玩家可以重按（Idempotency-Key 保護重複出手）。
    return state.phase === 'submitting' && state.roundNo === m.round_no ? 'submitting' : 'choosing';
  }
  return 'submitted';
}

function mergeFairness(prev: Fairness, next: Fairness | undefined): Fairness {
  if (!next) return prev;
  return {
    // commit 一旦公布就不該變；若真的變了，保留伺服器最新值但 UI 會顯示驗證失敗。
    server_seed_commit: next.server_seed_commit || prev.server_seed_commit,
    server_seed: next.server_seed ?? prev.server_seed,
    transcript_hash: next.transcript_hash ?? prev.transcript_hash,
  };
}

// ---------------------------------------------------------------------------
// 事件
// ---------------------------------------------------------------------------

function applyServerEvent(state: MatchMachineState, ev: ServerEventMessage): MatchMachineState {
  // 不是這場對局的事件一律丟掉（一條 WS 可以訂閱多個頻道，C02 §6）。
  if (state.matchId && ev.match_id && ev.match_id !== state.matchId) return state;

  // ★ `rematch_ready`（C02 §5 v1.2）刻意送到**舊**對局的頻道 ——
  //   那是此刻雙方都還訂閱著的頻道，因此它會通過上面那道 match_id 檢查。
  //   它處理的東西（導頁目標）與底下那個 switch 的「對局內狀態」不同層，
  //   所以獨立在 switch 之前處理，不當成一個 case。
  if (isRematchReady(ev)) return applyRematchReady(state, ev);

  switch (ev.event_type) {
    case 'match_created':
    case 'seat_joined': {
      const read = readSeats(ev.payload, state.seats);
      const seats = read.seats ?? state.seats;
      return {
        ...state,
        seats,
        error: seatKindError(read) ?? state.error,
        phase: state.phase === 'connecting' ? 'waiting_opponent' : state.phase,
      };
    }

    case 'seat_left': {
      const read = readSeats(ev.payload, state.seats);
      const seats = read.seats ?? state.seats;
      // ★ 對手斷線不改變 phase：斷線不等於棄權。
      return { ...state, seats, error: seatKindError(read) ?? state.error };
    }

    case 'match_started': {
      const read = readSeats(ev.payload, state.seats);
      const seats = read.seats ?? state.seats;
      const commit = readString(ev.payload, 'server_seed_commit');
      return {
        ...state,
        seats,
        error: seatKindError(read) ?? state.error,
        status: 'in_progress',
        variantKey: readString(ev.payload, 'variant_key') ?? state.variantKey,
        fairness: commit ? { ...state.fairness, server_seed_commit: commit } : state.fairness,
        phase: state.phase === 'ended' || state.phase === 'voided' ? state.phase : 'waiting_opponent',
      };
    }

    case 'round_started': {
      const roundNo = typeof ev.round_no === 'number' ? ev.round_no : state.roundNo + 1;
      // 重連後可能重播舊事件 → 舊回合一律忽略，否則畫面會倒退。
      if (roundNo < state.roundNo) return state;
      const commit = readString(ev.payload, 'server_seed_commit');
      const deadline = resolveRoundDeadline(ev);
      return {
        ...state,
        phase: 'choosing',
        status: 'in_progress',
        roundNo,
        legalActions: readActions(ev.payload) ?? state.legalActions,
        deadlineAt: deadline.at,
        deadlineSource: deadline.source,
        deadlinePassed: false,
        myPendingAction: null,
        pendingIdempotencyKey: null,
        lastResolved: null,
        fairness: commit ? { ...state.fairness, server_seed_commit: commit } : state.fairness,
        // 新回合開始 → 清掉上一回合的錯誤；但**這一個**事件自己的協定問題要留下。
        error: deadline.error,
      };
    }

    case 'action_received': {
      // C01 §6：visibility="seat_only"，只會送給出手者本人。
      // 也就是說：收到這個事件 = 伺服器確認**我**的出手，不可能是對手的。
      // 保險起見再擋一次 —— 若後端真的漏送了別人的，前端也不採用。
      if (typeof ev.seat === 'number' && state.mySeat !== null && ev.seat !== state.mySeat) return state;
      if (typeof ev.round_no === 'number' && ev.round_no !== state.roundNo) return state;
      if (state.phase !== 'submitting' && state.phase !== 'choosing') return state;
      return { ...state, phase: 'submitted' };
    }

    case 'round_resolved': {
      const roundNo = typeof ev.round_no === 'number' ? ev.round_no : state.roundNo;
      if (state.lastResolved && state.lastResolved.roundNo === roundNo) return state; // 重播，忽略
      const actions = readActionMap(ev.payload) ?? {};
      const winner = readRoundWinner(ev.payload); // ★ 回合層級用 `winner`（C02 §6.1）
      const scores = readScores(ev.payload) ?? state.scores;
      const entry: HistoryEntry = { round_no: roundNo, actions, winner };
      return {
        ...state,
        phase: 'revealing',
        roundNo,
        lastResolved: { roundNo, actions, winner, scores },
        history: upsertHistory(state.history, entry),
        scores,
        deadlinePassed: false,
        // 出手已經揭曉，pending 的意義結束。保留 myPendingAction 讓揭曉動畫可以做
        // 「你出的是 X」的對照。
        pendingIdempotencyKey: null,
      };
    }

    case 'match_ended': {
      const scores = readScores(ev.payload) ?? state.scores;
      const seed = readString(ev.payload, 'server_seed');
      const hash = readString(ev.payload, 'transcript_hash');
      // ★ 對局層級用 `winner_seat`，**不是** `winner`（C02 §6.1）。
      const outcome = readMatchOutcome(ev.payload);
      return {
        ...state,
        // 線路上的結束狀態是 `completed`（C02 §5 的 `view_for()`）。
        status: 'completed',
        // 若揭曉動畫還在播，讓它播完（REVEAL_DONE 會把 phase 帶到 ended）。
        phase: state.phase === 'revealing' ? 'revealing' : 'ended',
        finalOutcome: outcome,
        error: outcomeError(outcome, 'match_ended') ?? state.error,
        returns: readNumberMap(ev.payload, 'returns'),
        scores,
        deadlineAt: null,
        deadlineSource: 'none',
        deadlinePassed: false,
        fairness: {
          ...state.fairness,
          server_seed: seed ?? state.fairness.server_seed,
          transcript_hash: hash ?? state.fairness.transcript_hash,
        },
      };
    }

    case 'match_voided':
      return { ...state, phase: 'voided', status: 'voided', deadlineAt: null, deadlineSource: 'none' };

    case 'admin_action':
      return applyAdminAction(state, ev);

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// ★★ C02 §7：管理員的賽中動作（R-3）
// ---------------------------------------------------------------------------

/**
 * ★★ R-3 的缺陷本體：舊版是 `case "admin_action": return state;` —— 事件整個丟掉。
 *
 * ## 為什麼「丟掉」曾經看起來沒問題，現在卻會傷害玩家
 *
 * A-1 修好**之前**，`deadlineAt` 恆為 `null`：倒數環不渲染、`DEADLINE_PASSED`
 * 永遠不會被派發，於是「延長時限」延不延都一樣，缺陷**看不見**。
 * A-1 修好之後，`deadlineAt` 第一次有真值 ⇒ `DEADLINE_PASSED` 第一次真的會走到
 * ⇒ `deadlinePassed = true` ⇒ `canSubmit()` 回 false。
 *
 * 而後端 `admin/routes_game_admin.py::extend_deadline()` **確實**改寫了
 * `match.deadline_at` 並 publish 了 `admin_action`。前端丟掉這個事件的後果是：
 * 畫面繼續倒數到**舊**期限 → 歸零 → 顯示「時間到，等待伺服器裁定」
 * → **鎖住玩家不能出手**，而伺服器其實還在等他出手。
 *
 * **一個為了幫玩家而做的管理動作，效果是把玩家鎖死。**
 * 它從「看不見」變成「主動傷害玩家」，而中間沒有任何一行程式碼改變 ——
 * 改變的是**另一個接縫**。這就是為什麼「超出範圍」需要在相鄰的修復落地後重新評估。
 *
 * ## 一個事件型別、兩個動作（C02 §7.1）
 *
 * | 端點 | payload | 這裡做什麼 |
 * | :-- | :-- | :-- |
 * | `POST /matches/{id}/extend-deadline` | `{action:"extend_deadline", deadline_at}` | 換期限、清掉 `deadlinePassed` |
 * | `POST /matches/{id}/message` | `{action:"message", message}` | 進 `announcements`（需求 1.2.3） |
 *
 * ⚠️ **未知的 `action` 一律安靜忽略**，不記協定錯誤。
 * §7 的管理動作清單是會長大的，舊分頁遇到新動作時噴紅色橫幅，
 * 等於「後端每加一個功能，所有還開著的分頁都報錯」——
 * 那與「欄位形狀壞掉」是兩件完全不同的事。
 */
function applyAdminAction(state: MatchMachineState, ev: ServerEventMessage): MatchMachineState {
  switch (readString(ev.payload, 'action')) {
    case 'extend_deadline':
      return applyExtendDeadline(state, ev);
    case 'message':
      return applyAdminMessage(state, ev);
    default:
      return state;
  }
}

/**
 * ★ 延長出手時限。**期限的真相來源從這一刻起換成這個事件**——
 * 後端已經改寫 `match.deadline_at`，伺服器是照新期限裁定的。
 *
 * 三道守門，每一道都對應一種「畫面會說謊」的情形：
 *
 * 1. **已結束 / 已作廢的對局不得復活倒數。** 後端的前置條件是
 *    `status == "in_progress"`，所以這在正常情況下不會發生；
 *    但一個遲到的事件足以讓結束畫面上冒出一個跑動的倒數環。
 * 2. **舊回合的重播不得把倒數往回撥。** 與「重播的舊 `round_started`
 *    不得把倒數改回上一回合的期限」是同一條規則 —— 期限是回合層級的東西。
 * 3. **壞的 `deadline_at` 不猜。** 與 §6.2 的 `round_started` 不同，
 *    這裡**沒有**退化材料：`extra_seconds` 只存在於請求 body，不在事件裡
 *    （C02 §7.1）。猜短了就是把玩家的倒數往回撥 —— 那正是本段要修的傷害本身。
 *    所以：保留舊期限（伺服器至少不會比它更早裁定）＋**記錯誤**。
 */
function applyExtendDeadline(state: MatchMachineState, ev: ServerEventMessage): MatchMachineState {
  if (state.phase === 'ended' || state.phase === 'voided' || isTerminalStatus(state.status)) {
    return state;
  }
  if (typeof ev.round_no === 'number' && ev.round_no < state.roundNo) return state;

  const at = readTimestamp(ev.payload, 'deadline_at');
  if (!at) {
    return {
      ...state,
      error: { code: PROTOCOL_MISMATCH, message: ADMIN_DEADLINE_INVALID_MESSAGE },
    };
  }
  return {
    ...state,
    deadlineAt: at,
    deadlineSource: 'admin',
    // ★ 這一行就是 R-3 的修復：倒數已經歸零的玩家必須重新可以出手。
    //   `canSubmit()` 讀的是 `phase === 'choosing' && !deadlinePassed`。
    deadlinePassed: false,
  };
}

/**
 * ★ 對局內公告（需求 1.2.3）。reducer 只負責把它**收好、保持順序**。
 *
 * 「顯示在哪裡」是 `MatchPage` 的事 —— 見 `MatchAnnouncement` 的介面說明。
 *
 * 空的／壞掉的 `message` **不加一則空公告**：畫面上跳出一個沒有字的公告框，
 * 比沒有公告更糟（玩家會以為自己漏看了什麼）。改成記錯誤，讓它被看見。
 */
function applyAdminMessage(state: MatchMachineState, ev: ServerEventMessage): MatchMachineState {
  const message = readString(ev.payload, 'message');
  if (!message) {
    return {
      ...state,
      error: { code: PROTOCOL_MISMATCH, message: ADMIN_MESSAGE_INVALID_MESSAGE },
    };
  }
  return {
    ...state,
    ...withAnnouncements([
      ...state.announcements,
      {
        message,
        roundNo: typeof ev.round_no === 'number' ? ev.round_no : null,
        // `publish_raw()` 目前不補時間戳（golden 可以作證）——「沒有」就寫 null，
        // **不得**拿本機時鐘湊一個：reducer 是純函式，而且那會是假資料。
        occurredAt: typeof ev.occurred_at === 'string' ? ev.occurred_at : null,
      },
    ]),
  };
}

/** C02 §5 v1.2 的 `rematch_ready`。它已經在 `EventType` 裡（`api/types.ts`）。 */
function isRematchReady(ev: ServerEventMessage): boolean {
  return ev.event_type === 'rematch_ready';
}

// ---------------------------------------------------------------------------
// ★ C02 §6.2（v1.3）：`round_started` 的期限
// ---------------------------------------------------------------------------

/**
 * ★★ A-1 的缺陷本體就在這裡。
 *
 * 舊版是**無條件覆寫**：`deadlineAt: readString(ev.payload, 'deadline_at') ?? null`。
 * 缺陷當時後端的 `round_started` 只送 `move_timeout_ms`（引擎是純函式，不得讀牆上時鐘），
 * 從來沒有送過 `deadline_at` —— 於是**每一次 `round_started` 都把倒數打回 `null`**。
 * （C02 §6.2 裁決後，服務層已在 publish 前補上 `deadline_at`；
 * 但退化路徑仍然必須留著 —— 見下面「為什麼這裡 fail-open」。）
 * 症狀是：倒數環整個不渲染、「時間到」永遠不出現、玩家按鈕一直亮著，
 * 然後回合突然自己結算了。唯一還看得到倒數的路徑是重新整理／重連
 * （走 `GET /matches/{id}` 的**頂層** `deadline_at`）—— 這正是它活這麼久沒被發現的原因，
 * 而 F-3（再戰導頁）修好之後，再戰產生的新對局只走事件路徑，它變成 100% 觸發。
 *
 * ## 為什麼這裡 fail-open，而 §6.1 fail-closed —— 同一份契約的兩種態度
 *
 * C02 §6.1 對 `match_ended` 缺 `winner_seat` 的要求是「記錯誤 + 顯示**結果未知**，
 * **不得**猜」；§6.2 對 `round_started` 缺 `deadline_at` 的要求卻是
 * 「記錯誤 + **仍然**退化推導，不得直接放棄倒數」。兩者看似矛盾，
 * 差別在**失敗代價不對稱**（契約明文寫了理由）：
 *
 * | | 猜錯的代價 | 不猜的代價 |
 * | :-- | :-- | :-- |
 * | §6.1 勝負 | **對玩家說謊**：把「我讀不到」講成「和局」，而且是最終結論，無法自我修正 | 少一句話，玩家可以去看回放 |
 * | §6.2 期限 | 倒數可能差幾百毫秒（誤差方向還是**對玩家有利**：畫面比伺服器早到期） | **玩家在毫不知情的情況下被伺服器代打，輸掉整場** |
 *
 * 也就是說：§6.1 的沉默是安全的、猜測是危險的；§6.2 剛好相反。
 * 兩條規則其實是同一個原則 ——「**往代價小的那一邊倒**」——
 * 在兩種不同的代價結構下得到的兩個結論。
 *
 * 但退化路徑**必須吵**（記 `PROTOCOL_MISMATCH`），否則它就會變成第二個 §6.1：
 * 一個安靜地活很久、沒有人有義務發現的缺陷。
 */
interface RoundDeadline {
  at: string | null;
  source: DeadlineSource;
  error: { code: string; message: string } | null;
}

function resolveRoundDeadline(ev: ServerEventMessage): RoundDeadline {
  // 1. 契約要求的正常路徑：payload 的絕對時刻。
  const explicit = readTimestamp(ev.payload, 'deadline_at');
  if (explicit) return { at: explicit, source: 'event', error: null };

  // 2. 退化推導。**不是**「收到時才開始倒數」——
  //    那會讓網路延遲與重連積壓系統性地多給時間，而伺服器是照絕對期限裁定的。
  //    用事件自己的 `occurred_at` 當基準，誤差只剩伺服器產生事件到寫入的那一小段。
  const derived = deriveDeadline(ev);
  if (derived) {
    return {
      at: derived,
      source: 'derived',
      error: { code: PROTOCOL_MISMATCH, message: DEADLINE_DERIVED_MESSAGE },
    };
  }

  // 3. 連推導都做不到（缺 `occurred_at` 或 `move_timeout_ms`）。
  //    reducer 是純函式、沒有時鐘，所以這裡**不能**拿本機時間硬湊一個出來。
  //    退路是 `useMatch` 本來就會走的 `GET /matches/{id}`（頂層 `deadline_at`）。
  return {
    at: null,
    source: 'unavailable',
    error: { code: PROTOCOL_MISMATCH, message: DEADLINE_UNAVAILABLE_MESSAGE },
  };
}

/** `occurred_at + move_timeout_ms` → 絕對時刻。任何一半缺了/壞了就回 null。 */
function deriveDeadline(ev: ServerEventMessage): string | null {
  const occurredAt = typeof ev.occurred_at === 'string' ? Date.parse(ev.occurred_at) : Number.NaN;
  const timeoutMs = readPositiveNumber(ev.payload, 'move_timeout_ms');
  if (!Number.isFinite(occurredAt) || timeoutMs === null) return null;
  return new Date(occurredAt + timeoutMs).toISOString();
}

/**
 * ★ 再戰：把新對局的 id 收進 state，**不做導頁**。
 *
 * 分工（orchestrator 釘死的介面）：
 *   FE-A（本檔）只負責把 `rematchMatchId` 填對；
 *   FE-B（`MatchPage` / `useMatch`）監看它，非 null 且不等於目前 `matchId` 時
 *   導向 `/m/<新 id>`。兩邊都不跨界。
 *
 * `new_match_id` **必須是非空字串**才寫入。缺 id 的再戰事件是壞掉的訊號，
 * 不是可以猜的東西 —— 猜錯就是把玩家丟進一場不存在的對局。
 * 因此壞事件一律忽略，並把 warning 記進 `state.error`
 * （reducer 是純函式，不在這裡 console.warn；記進 state 也才測得到）。
 */
function applyRematchReady(state: MatchMachineState, ev: ServerEventMessage): MatchMachineState {
  const newMatchId = readString(ev.payload, 'new_match_id');
  if (!newMatchId) {
    return {
      ...state,
      error: {
        code: REMATCH_EVENT_INVALID,
        message: 'rematch_ready without a usable new_match_id',
      },
    };
  }
  return { ...state, rematchMatchId: newMatchId };
}

function upsertHistory(history: HistoryEntry[], entry: HistoryEntry): HistoryEntry[] {
  const idx = history.findIndex((h) => h.round_no === entry.round_no);
  if (idx >= 0) {
    const next = history.slice();
    next[idx] = entry;
    return next;
  }
  return [...history, entry].sort((a, b) => a.round_no - b.round_no);
}

// ---------------------------------------------------------------------------
// 防禦性的 payload 讀取（payload 在契約裡是 dict，不能假設形狀）
// ---------------------------------------------------------------------------

function readString(payload: Record<string, unknown>, key: string): string | null {
  const v = payload?.[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/**
 * 只接受**解析得出來**的 ISO-8601 時刻。
 *
 * 為什麼不是 `readString()` 就好：一個解析不出來的 `deadline_at`（例如 `"soon"`、
 * 或某個版本送成了 `"10000"`）會讓 `useMatch` 的 `Date.parse()` 回 `NaN`
 * → `remainingMs` 變 `null` → **倒數消失，而且沒有任何人記錯誤**，
 * 症狀與 A-1 一模一樣。壞值必須跟缺值走同一條退化路徑。
 */
function readTimestamp(payload: Record<string, unknown>, key: string): string | null {
  const v = readString(payload, key);
  return v !== null && Number.isFinite(Date.parse(v)) ? v : null;
}

/** 正的有限數字，其餘（含 0、負數、字串 `"10000"`）一律當作沒有。 */
function readPositiveNumber(payload: Record<string, unknown>, key: string): number | null {
  const v = payload?.[key];
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

function readActions(payload: Record<string, unknown>): ActionKey[] | null {
  const v = payload?.legal_actions;
  if (!Array.isArray(v)) return null;
  const out = v.filter((x): x is string => typeof x === 'string');
  return out.length > 0 ? out : null;
}

function readActionMap(payload: Record<string, unknown>): Record<string, ActionKey> | null {
  const v = payload?.actions;
  if (!v || typeof v !== 'object') return null;
  const out: Record<string, ActionKey> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val === 'string') out[k] = val;
  }
  return out;
}

function readScores(payload: Record<string, unknown>): Record<string, number> | null {
  return readNumberMap(payload, 'scores');
}

function readNumberMap(payload: Record<string, unknown>, key: string): Record<string, number> | null {
  const v = payload?.[key];
  if (!v || typeof v !== 'object') return null;
  const out: Record<string, number> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val === 'number' && Number.isFinite(val)) out[k] = val;
  }
  return out;
}

/**
 * ★ `round_resolved` 的**回合層級**勝者：欄位名是 `winner`（C02 §6.1）。
 *
 * 平手是 `winner: null` **且** `draw: true`（C02 §5 已釘死）。
 * 回合層級沒有「結果未知」這一態 —— 回合的輸贏另外有 `actions` 與 `scores` 兜著，
 * 而且它不會變成整場對局的結論。
 *
 * **這支 reader 絕對不可以拿去讀 `match_ended`**，反之亦然（見 `readMatchOutcome`）。
 */
function readRoundWinner(payload: Record<string, unknown>): Seat | null {
  const v = payload?.winner;
  return isSeat(v) ? v : null;
}

/**
 * ★ `match_ended` 的**對局層級**勝者：欄位名是 `winner_seat`（C02 §6.1）。
 *
 * 兩個事件用兩個名字是**刻意的**、契約明文記載的事實：
 * `winner_seat` 與 DB 欄位 `match.winner_seat`、`state.public["winner_seat"]`、
 * 管理端 `force-end` 的 body 一致 —— 整條對局層級的資料鏈上都叫這個名字。
 * 契約因此明令：**客戶端不得用同一個 reader 讀兩者。**
 *
 * 三態的判斷順序不可以顛倒：先問「key 在不在」，再看值。
 * 顛倒過來（先看值是不是 null）就會把「欄位不存在」摺進「平手」，
 * 那正是 F-4 的缺陷本身。
 */
function readMatchOutcome(payload: Record<string, unknown>): MatchOutcome {
  if (!payload || typeof payload !== 'object' || !('winner_seat' in payload)) {
    return { kind: 'unknown', reason: 'winner_seat_absent' };
  }
  const v = payload.winner_seat;
  if (v === null) return { kind: 'draw' };
  if (v === undefined) return { kind: 'unknown', reason: 'winner_seat_absent' };
  if (isSeat(v)) return { kind: 'decided', winner: v };
  // key 在、值卻不是座位也不是 null（例如字串 "0"）。同樣**不得**當成和局。
  return { kind: 'unknown', reason: 'winner_seat_malformed' };
}

function isSeat(v: unknown): v is Seat {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0;
}

function outcomeError(
  outcome: MatchOutcome | null,
  source: 'match_ended' | 'observation.public',
): { code: string; message: string } | null {
  if (!outcome || outcome.kind !== 'unknown') return null;
  // C02 §6.1：「必須記錄錯誤」。reducer 是純函式（見檔頭），所以錯誤**記進 state**，
  // 由 useMatch 的副作用層去 console/telemetry —— 不在這裡直接 log。
  return {
    code: PROTOCOL_MISMATCH,
    message: `${source}: ${outcome.reason}`,
  };
}

/**
 * ★★ R-4：`seats` 在線路上有**兩種形狀**，而且兩種都是對的。
 *
 * | 來源 | 形狀 | 證據 |
 * | :-- | :-- | :-- |
 * | `match_started.payload.seats` | `[0, 1]` —— **座位號** | golden `wire` / 三個 `scenarios` 都是 |
 * | `GET /matches/{id}`、`seat_joined` / `seat_left` | `[{seat, display_name, kind, connected}]` | C02 §5 明列 |
 *
 * 舊版只認得後者，於是 `match_started` 之後 `state.seats` 是 `[]`。
 * 這是 F-4（`winner_seat` vs `winner`）、A-1（`deadline_at` vs `move_timeout_ms`）
 * 之後的**第三個同型接縫**：兩邊各自都對，錯的是接縫，而且兩邊的測試都是綠的。
 *
 * ## 為什麼它在 production 還沒炸 —— 以及為什麼那不是安慰
 *
 * 呼叫端寫的是 `readSeats(…) ?? state.seats`，而 HYDRATE 先跑過了 ——
 * 讀不到就沿用投影過的座位，看起來一切正常。**潛伏，正是 F-4 當初存活的方式**：
 * 它只在「沒有 HYDRATE、只有事件」的路徑上現形，而那條路徑遲早會出現
 * （F-3 修好之後的再戰導頁就差一點走上它）。
 *
 * ## 這個修法的邊界：讀得懂 ≠ 蓋掉
 *
 * 座位號裡**沒有** `display_name` / `kind` / `connected`。
 * 拿座位號硬造一份完整的 `SeatInfo` 覆蓋上去，對手的暱稱就會在開局那一瞬間
 * 從真名變成 `Seat 1` —— 那是把一個潛伏的缺陷換成一個所有人都看得到的缺陷。
 * 所以：**座位號只提供座位號**，其餘欄位一律沿用 `known` 裡已經知道的那一份，
 * 真的不知道時才用佔位值。
 *
 * @param known 目前已知的座位（`state.seats`）。座位號形狀靠它補齊其餘欄位。
 */
/**
 * `readSeats()` 的回傳：座位**和**「有沒有讀不懂的東西」。
 *
 * 為什麼不是單純回 `SeatInfo[] | null`：讀不懂的 `kind` 必須被記成錯誤（見
 * {@link SEAT_KIND_UNKNOWN_MESSAGE}），而 reducer 是純函式、不能在這裡 log。
 * 「解析結果」與「解析過程中發現的協定問題」一起回傳，呼叫端才有機會把它寫進 state。
 */
interface SeatsRead {
  /** 讀得懂的座位。一個都讀不出來 ⇒ `null`（呼叫端沿用既有座位，不清空）。 */
  seats: SeatInfo[] | null;
  /** 讀不懂的 `kind`（線路上的原字串）。空陣列 = 全部讀得懂。 */
  unknownKinds: string[];
}

/** `payload.seats` 根本不是陣列時的回傳。凍結成常數，避免每次配置新物件。 */
const NO_SEATS: SeatsRead = { seats: null, unknownKinds: [] };

/** 把 `readSeats()` 發現的未知 kind 轉成 `state.error`。全部讀得懂就回 null。 */
function seatKindError(read: SeatsRead): { code: string; message: string } | null {
  if (read.unknownKinds.length === 0) return null;
  return {
    code: PROTOCOL_MISMATCH,
    message: `${SEAT_KIND_UNKNOWN_MESSAGE}: ${read.unknownKinds.join(', ')}`,
  };
}

function readSeats(payload: Record<string, unknown>, known: SeatInfo[] = []): SeatsRead {
  const v = payload?.seats;
  if (!Array.isArray(v)) return NO_SEATS;
  const out: SeatInfo[] = [];
  const unknownKinds: string[] = [];
  for (const raw of v) {
    const parsed = readSeatEntry(raw, known, unknownKinds);
    if (parsed) out.push(parsed);
  }
  return { seats: out.length > 0 ? out : null, unknownKinds };
}

/**
 * 一個座位項目：可能是座位號（`0`），也可能是完整物件。讀不懂就回 null。
 *
 * @param unknownKinds out-param：讀不懂的 `kind`（原字串）會被 push 進來，
 *   由呼叫端轉成 `state.error`。reducer 是純函式，所以這裡不 log。
 */
function readSeatEntry(raw: unknown, known: SeatInfo[], unknownKinds: string[]): SeatInfo | null {
  // 形狀 A：座位號。`match_started` 送的就是這個。
  if (isSeat(raw)) {
    return (
      known.find((s) => s.seat === raw) ?? {
        seat: raw,
        display_name: `Seat ${raw}`,
        // ★ 座位號**沒有攜帶 kind**，所以答案是「不知道」（null），不是 'guest'。
        //   這不是協定不相容（C02 §5 的座位號形狀本來就沒有 kind），因此不記錯誤。
        kind: null,
        connected: true,
      }
    );
  }
  // 形狀 B：完整的座位物件（C02 §5 的 `seats[]`）。這一份是權威，直接採用。
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<SeatInfo>;
  if (typeof s.seat !== 'number') return null;
  const entry: SeatInfo = {
    seat: s.seat,
    display_name: typeof s.display_name === 'string' ? s.display_name : `Seat ${s.seat}`,
    // ★★ 第七個宣告點：值域的**執行期**副本（前六個都只是型別／約束）。
    //   逐字列出三個成員而不是寫 `SEAT_KINDS.includes(...)`，理由有二：
    //     1. `src/test/response-contract.test.ts` 的守衛是**讀原始碼**比對這一行的，
    //        它需要看得見這三個字面量；
    //     2. 值域是 expand-only（CONSTRAINTS §5，已預告 bot / observer），
    //        加值時這一行必須跟著改 —— 讓它顯眼，不要藏在一個泛用的 includes 後面。
    //   ★ 讀不懂 ⇒ null（明確的 unknown）＋ 記錯誤。**絕不**挑一個看起來合理的替代值：
    //     那正是 C02 §6.1 禁止的模式，也是這一輪要修的缺陷本體
    //     （後端送 "human" 被靜靜改寫成 "guest"）。
    kind: s.kind === 'human' || s.kind === 'guest' || s.kind === 'ai' ? s.kind : null,
    connected: s.connected !== false,
  };
  if (entry.kind === null) {
    // 物件形狀**應該**帶 kind（C02 §5）。沒帶或帶了不認得的值都是協定不相容。
    if (typeof s.kind === 'string') entry.kind_raw = s.kind;
    unknownKinds.push(s.kind === undefined ? '(absent)' : JSON.stringify(s.kind));
  }
  return entry;
}

// ---------------------------------------------------------------------------
// Selectors（UI 只准透過這些讀狀態，把「什麼可以畫」集中管理）
// ---------------------------------------------------------------------------

/** 三顆大按鈕現在可不可按。 */
export function canSubmit(state: MatchMachineState): boolean {
  return state.phase === 'choosing' && !state.deadlinePassed;
}

/** 是否顯示「等待對手」畫面。這個畫面不得含任何對手資訊。 */
export function isWaitingForOpponent(state: MatchMachineState): boolean {
  return state.phase === 'submitted';
}

/**
 * ★ 最新一則對局內公告（C02 §7.1、需求 1.2.3）。沒有就是 `null`。
 *
 * 給只想顯示「最新一則」的 UI 用。要顯示全部就直接讀 `state.announcements`
 * （它已經是依收到順序排好的）。
 */
export function latestAnnouncement(state: MatchMachineState): MatchAnnouncement | null {
  return state.announcements.length > 0 ? state.announcements[state.announcements.length - 1] : null;
}

export function opponentSeat(state: MatchMachineState): SeatInfo | null {
  if (state.mySeat === null) return null;
  return state.seats.find((s) => s.seat !== state.mySeat) ?? null;
}

export function mySeatInfo(state: MatchMachineState): SeatInfo | null {
  if (state.mySeat === null) return null;
  return state.seats.find((s) => s.seat === state.mySeat) ?? null;
}

export function myScore(state: MatchMachineState): number {
  return state.mySeat === null ? 0 : (state.scores[String(state.mySeat)] ?? 0);
}

export function opponentScore(state: MatchMachineState): number {
  const opp = opponentSeat(state);
  return opp ? (state.scores[String(opp.seat)] ?? 0) : 0;
}

export type RoundOutcome = 'win' | 'lose' | 'draw';

/**
 * **回合**的輸贏。`winner === null` 就是平手 —— 回合層級沒有第三態
 * （C02 §5 已釘死 `round_resolved` 的平手是 `winner: null` + `draw: true`）。
 *
 * ⚠️ **不要拿它算整場對局的結果。** 對局層級請用 `matchResultFor()` /
 * `selectMatchResult()`，那裡多一個「結果未知」，而這裡沒有。
 */
export function outcomeFor(seat: Seat | null, winner: Seat | null): RoundOutcome {
  if (winner === null) return 'draw';
  if (seat === null) return 'draw';
  return winner === seat ? 'win' : 'lose';
}

/**
 * ★ 整場對局的結果，站在「我」的角度。**這是 UI 唯一該用的入口。**
 *
 * 回 `'unknown'` 的三種情形，對玩家而言意義相同（我們無法告訴你誰贏了），
 * 差別留在 `state.finalOutcome.reason` 供診斷：
 *   * 收到的 `match_ended` 沒有 `winner_seat`（協定不相容）；
 *   * 值壞掉（不是座位也不是 null）；
 *   * 對局已結束卻沒有任何結束資訊（例如 `abandoned` 的 hydrate）。
 *
 * **絕不**在讀不到時回 `'draw'` —— 那正是 C02 §6.1 明文禁止的退化。
 */
export function matchResultFor(state: MatchMachineState): MatchResult {
  const outcome = state.finalOutcome;
  if (!outcome) return 'unknown';
  if (outcome.kind === 'unknown') return 'unknown';
  if (outcome.kind === 'draw') return 'draw';
  if (state.mySeat === null) return 'unknown';
  return outcome.winner === state.mySeat ? 'win' : 'lose';
}

/**
 * ★ 讓**編譯器**強迫 UI 處理「結果未知」。
 *
 * `Record<MatchResult, T>` 少一個 key 就編譯失敗，所以
 * 「忘了寫 unknown 那一支」在型別檢查階段就會被擋下來，而不是等到
 * 生產環境某一天送來一個讀不懂的 payload 才發現畫面在說謊。
 *
 * ```tsx
 * <h2>{selectMatchResult(state, {
 *   win: t('match.won'),
 *   lose: t('match.lost'),
 *   draw: t('match.drawn'),
 *   unknown: t('match.resultUnknown'),
 * })}</h2>
 * ```
 */
export function selectMatchResult<T>(state: MatchMachineState, cases: Record<MatchResult, T>): T {
  return cases[matchResultFor(state)];
}

/** 揭曉動畫要顯示的兩手。只有在 lastResolved 存在時才有值 —— 型別上就擋住提早揭曉。 */
export function revealPair(state: MatchMachineState): { mine: ActionKey | null; theirs: ActionKey | null } | null {
  const r = state.lastResolved;
  if (!r || state.mySeat === null) return null;
  const opp = opponentSeat(state);
  return {
    mine: r.actions[String(state.mySeat)] ?? state.myPendingAction,
    theirs: opp ? (r.actions[String(opp.seat)] ?? null) : null,
  };
}

/**
 * ★ 資訊邊界的單一檢查點。
 * 回傳 true 表示「現在畫面上可以出現對手的動作」。UI 元件必須先問過這個函式。
 */
export function mayRenderOpponentAction(state: MatchMachineState, roundNo: number): boolean {
  if (state.phase === 'ended' || state.phase === 'voided') return true;
  if (state.phase === 'revealing' || state.phase === 'round_summary') {
    return state.lastResolved !== null && roundNo <= state.lastResolved.roundNo;
  }
  // choosing / submitting / submitted / waiting / connecting：只有已結算的舊回合可以看
  return state.history.some((h) => h.round_no === roundNo);
}
